import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';

import ChatSidebar from './ChatSidebar';
import ChatArea from './ChatArea';
import AgentPicker from './AgentPicker';
import PromptPicker from './PromptPicker';
import FolderSettings from './FolderSettings';
import ProjectBrowser from './ProjectBrowser';
import ProjectOverviewPanel from './ProjectOverviewPanel';

const DEFAULT_MODELS = [
  { id: 'gpt-5.5', name: 'GPT-5.5', description: 'Nieuwste flagship, 1M context' },
  { id: 'gpt-5.4', name: 'GPT-5.4', description: 'Vorige flagship, 1M context' },
  { id: 'gpt-5.4-mini', name: 'GPT-5.4 Mini', description: 'Snel en voordelig, 400k context' },
  { id: 'gpt-5.4-pro', name: 'GPT-5.4 Pro', description: 'Beste kwaliteit, meer compute' },
  { id: 'gpt-5.4-nano', name: 'GPT-5.4 Nano', description: 'Ultravoordelig, laagste kosten' },
  { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', description: 'Anthropic flagship' },
  { id: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5', description: 'Snel en voordelig' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'Google flagship, 1M context' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: 'Google, snel en goedkoop' },
];

export default function AIChat() {
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const userName = accounts?.[0]?.name || '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [conversations, setConversations] = useState([]);
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [models] = useState(DEFAULT_MODELS);
  const [model, setModel] = useState('gpt-5.5');
  const [isStreaming, setIsStreaming] = useState(false);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [promptPickerOpen, setPromptPickerOpen] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [agentToInsert, setAgentToInsert] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [folderSettingsOpen, setFolderSettingsOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Participants state
  const [participants, setParticipants] = useState([]);

  // Client state
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState(null);

  // Project Hub state
  const [showProjectBrowser, setShowProjectBrowser] = useState(false);
  const [showProjectOverview, setShowProjectOverview] = useState(false);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [isMobile]);

  // Ctrl+Z / Cmd+Z buiten tekstvelden = laatste actie terugdraaien
  useEffect(() => registreerOngedaanSneltoets(), []);

  // ─── Data Fetching ─────────────────────────────────────────

  const fetchConversations = useCallback(async (folderId = undefined, q = undefined) => {
    if (!userEmail) return;
    try {
      let url = `${AI_ENGINE_URL}/api/v1/chat/conversations?user_email=${encodeURIComponent(userEmail)}`;
      const activeFolderId = folderId !== undefined ? folderId : selectedFolderId;
      const activeQuery = q !== undefined ? q : searchQuery;
      if (activeFolderId !== null && activeFolderId !== undefined) url += `&folder_id=${activeFolderId}`;
      if (activeQuery) url += `&q=${encodeURIComponent(activeQuery)}`;
      const res = await authFetch(url);
      if (res.ok) setConversations(await res.json());
    } catch (err) {
      console.error('[AIChat] Failed to fetch conversations:', err);
    }
  }, [userEmail, selectedFolderId, searchQuery]);

  const fetchArchivedConversations = useCallback(async () => {
    if (!userEmail) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations?user_email=${encodeURIComponent(userEmail)}&archived=true`);
      if (res.ok) setArchivedConversations(await res.json());
    } catch (err) {
      console.error('[AIChat] Failed to fetch archived:', err);
    }
  }, [userEmail]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/agents`);
      if (res.ok) setAgents(await res.json());
    } catch (err) { console.error('[AIChat] Failed to fetch agents:', err); }
  }, []);

  const fetchPrompts = useCallback(async () => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/prompts`);
      if (res.ok) setPrompts(await res.json());
    } catch (err) { console.error('[AIChat] Failed to fetch prompts:', err); }
  }, []);

  const fetchFolders = useCallback(async () => {
    if (!userEmail) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders?user_email=${encodeURIComponent(userEmail)}`);
      if (res.ok) setFolders(await res.json());
    } catch (err) { console.error('[AIChat] Failed to fetch folders:', err); }
  }, [userEmail]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten`);
      if (res.ok) {
        const data = await res.json();
        const mapped = (data.klanten || [])
          .filter((r) => r.fields?.Account)
          .map((r) => ({
            id: r.id,
            name: r.fields.Account,
            logo: r.fields?.Beeldmerk?.[0]?.thumbnails?.small?.url || r.fields?.Beeldmerk?.[0]?.url || null,
            status: r.fields?.Status || '',
            sector: r.fields?.Sector || '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setClients(mapped);
      }
    } catch (err) { console.error('[AIChat] Failed to fetch clients:', err); }
  }, []);

  const loadConversation = useCallback(async (convId) => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations/${convId}?user_email=${encodeURIComponent(userEmail)}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setParticipants(data.participants || []);
        setModel(data.model || 'gpt-5.5');
        setSelectedClientId(data.client_id || null);
        setSelectedClientName(data.client_name || null);
        if (data.agent_id) {
          const agent = agents.find((a) => a.id === data.agent_id);
          setCurrentAgent(agent || null);
        } else {
          setCurrentAgent(null);
        }
      }
    } catch (err) { console.error('[AIChat] Failed to load conversation:', err); }
  }, [agents, userEmail]);

  useEffect(() => {
    fetchConversations();
    fetchArchivedConversations();
    fetchAgents();
    fetchPrompts();
    fetchFolders();
    fetchClients();
  }, [fetchConversations, fetchArchivedConversations, fetchAgents, fetchPrompts, fetchFolders, fetchClients]);

  useEffect(() => {
    if (selectedConvId) loadConversation(selectedConvId);
  }, [selectedConvId, loadConversation]);

  // ─── Folder Actions ─────────────────────────────────────────

  const handleSelectFolder = (folderId) => {
    setSelectedFolderId(folderId);
    setShowProjectBrowser(false);
    // Auto-show project overview when selecting a project folder
    const folder = folders.find((f) => f.id === folderId);
    if (folder?.project_id) {
      setShowProjectOverview(true);
      setSelectedConvId(null);
      setMessages([]);
    } else {
      setShowProjectOverview(false);
    }
    fetchConversations(folderId, searchQuery);
  };

  const handleEditFolder = (folder) => { setEditingFolder(folder); setFolderSettingsOpen(true); };
  const handleNewFolder = () => { setEditingFolder(null); setFolderSettingsOpen(true); };

  const handleDeleteFolder = async (folderId) => {
    if (!(await bevestig({ titel: 'Folder verwijderen?', tekst: 'Gesprekken worden niet verwijderd.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/folders/${folderId}?user_email=${encodeURIComponent(userEmail)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      toast.success('Folder verwijderd');
      if (selectedFolderId === folderId) setSelectedFolderId(null);
      fetchFolders();
      fetchConversations(null, searchQuery);
    } catch (err) { toast.error(err.message); }
  };

  const handleFolderSaved = () => { fetchFolders(); fetchConversations(); };
  const handleSearch = (q) => { setSearchQuery(q); fetchConversations(selectedFolderId, q); };

  // ─── Project Pin/Unpin Actions ─────────────────────────────

  const handlePinProject = async (projectId) => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/projects/${projectId}/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail }),
      });
      if (res.ok) {
        toast.success('Project gepind');
        fetchFolders();
      } else if (res.status === 409) {
        toast('Project is al gepind');
      } else {
        toast.error('Pinnen mislukt');
      }
    } catch (err) { toast.error('Pinnen mislukt'); }
  };

  const handleUnpinProject = async (projectId) => {
    if (!(await bevestig({ titel: 'Project loskoppelen?', tekst: 'De folder en bestanden worden verwijderd.', knop: 'Loskoppelen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(
        `${AI_ENGINE_URL}/api/v1/chat/projects/${projectId}/unpin?user_email=${encodeURIComponent(userEmail)}`,
        { method: 'DELETE' },
      );
      if (res.ok) {
        toast.success('Project losgekoppeld');
        if (selectedFolderId) {
          const folder = folders.find((f) => f.project_id === projectId);
          if (folder && folder.id === selectedFolderId) {
            setSelectedFolderId(null);
            setShowProjectOverview(false);
          }
        }
        fetchFolders();
        fetchConversations();
      } else {
        toast.error('Unpin mislukt');
      }
    } catch (err) { toast.error('Unpin mislukt'); }
  };

  // ─── Conversation Actions ──────────────────────────────────

  const updateConversation = async (convId, data) => {
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations/${convId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      return res.ok;
    } catch (err) { return false; }
  };

  const handleNewChat = async (agent = null) => {
    setShowProjectBrowser(false);
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail, user_name: userName,
          agent_id: agent?.id || null, model: agent?.model || model,
          folder_id: selectedFolderId, client_id: selectedClientId, client_name: selectedClientName,
        }),
      });
      if (res.ok) {
        const conv = await res.json();
        setSelectedConvId(conv.id);
        setMessages([]);
        setParticipants([]);
        setCurrentAgent(agent);
        if (agent?.model) setModel(agent.model);
        fetchConversations();
        fetchFolders();
        if (isMobile) setSidebarOpen(false);
      }
    } catch (err) { toast.error('Gesprek aanmaken mislukt'); }
  };

  const handleDeleteConversation = async (convId) => {
    if (!(await bevestig({ titel: 'Gesprek verwijderen?', tekst: 'Berichten en bijlagen zijn daarna weg.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations/${convId}?user_email=${encodeURIComponent(userEmail)}`, { method: 'DELETE' });
      if (!res.ok) { toast.error('Verwijderen mislukt'); return; }
      if (selectedConvId === convId) { setSelectedConvId(null); setMessages([]); setParticipants([]); }
      fetchConversations(); fetchArchivedConversations(); fetchFolders();
    } catch (err) { toast.error('Verwijderen mislukt'); }
  };

  const handleArchiveConversation = async (convId, { stil = false } = {}) => {
    if (await updateConversation(convId, { is_archived: true })) {
      if (selectedConvId === convId) { setSelectedConvId(null); setMessages([]); setParticipants([]); }
      fetchConversations(); fetchArchivedConversations(); fetchFolders();
      // Ongedaan maken = terugzetten uit archief
      if (!stil) biedOngedaanAan('Gesprek gearchiveerd', () => handleUnarchiveConversation(convId, { stil: true }));
    } else if (stil) { throw new Error('Archiveren mislukt'); }
    else { toast.error('Archiveren mislukt'); }
  };

  const handleUnarchiveConversation = async (convId, { stil = false } = {}) => {
    if (await updateConversation(convId, { is_archived: false })) {
      fetchConversations(); fetchArchivedConversations(); fetchFolders();
      // Ongedaan maken = opnieuw archiveren
      if (!stil) biedOngedaanAan('Gesprek teruggezet', () => handleArchiveConversation(convId, { stil: true }));
    } else if (stil) { throw new Error('Terughalen mislukt'); }
    else { toast.error('Terughalen mislukt'); }
  };

  const handlePin = async (convId, pinned) => {
    if (await updateConversation(convId, { is_pinned: pinned })) {
      fetchConversations();
      toast.success(pinned ? 'Gesprek vastgezet' : 'Gesprek losgemaakt');
    }
  };

  const handleRename = async (convId, newTitle) => {
    if (await updateConversation(convId, { title: newTitle })) {
      fetchConversations();
      toast.success('Naam gewijzigd');
    }
  };

  const handleDetachFromFolder = async (convId) => {
    if (await updateConversation(convId, { folder_id: null })) {
      fetchConversations(); fetchFolders();
      toast.success('Chat losgemaakt van project');
    }
  };

  const handleMoveToFolder = async (convId, folderId) => {
    if (await updateConversation(convId, { folder_id: folderId })) {
      fetchConversations(); fetchFolders();
      const folder = folders.find((f) => f.id === folderId);
      toast.success(`Chat verplaatst naar ${folder?.name || 'project'}`);
    }
  };

  const handleNewChatInFolder = async (folderId) => {
    const folder = folders.find((f) => f.id === folderId);
    setShowProjectBrowser(false);
    setShowProjectOverview(false);
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail, user_name: userName,
          agent_id: folder?.agent_id || null,
          model: folder?.model || model,
          folder_id: folderId,
          client_id: folder?.client_id || null,
          client_name: folder?.client_name || null,
        }),
      });
      if (res.ok) {
        const conv = await res.json();
        setSelectedConvId(conv.id);
        setMessages([]);
        setParticipants([]);
        setSelectedFolderId(folderId);
        if (folder?.client_id) {
          setSelectedClientId(folder.client_id);
          setSelectedClientName(folder.client_name || null);
        }
        if (folder?.model) setModel(folder.model);
        if (folder?.agent_id) {
          const agent = agents.find((a) => a.id === folder.agent_id);
          setCurrentAgent(agent || null);
        }
        fetchConversations(folderId);
        fetchFolders();
        if (isMobile) setSidebarOpen(false);
      }
    } catch (err) { toast.error('Gesprek aanmaken mislukt'); }
  };

  const handleStartGroupChat = async (convId) => {
    const email = window.prompt('E-mailadres van de gebruiker die je wilt uitnodigen:');
    if (!email) return;
    try {
      const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations/${convId}/participants?requester_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: email, user_name: '' }),
      });
      if (res.ok) {
        fetchConversations();
        toast.success(`${email} uitgenodigd voor groepschat`);
      } else { toast.error('Uitnodigen mislukt'); }
    } catch (err) { toast.error('Uitnodigen mislukt'); }
  };

  const handleSelectConversation = (convId) => {
    setSelectedConvId(convId);
    setShowProjectBrowser(false);
    setShowProjectOverview(false);
    if (isMobile) setSidebarOpen(false);
  };

  const handleModelChange = async (newModel) => {
    setModel(newModel);
    if (selectedConvId) await updateConversation(selectedConvId, { model: newModel });
  };

  const handleClientChange = async (clientId) => {
    const cl = clients.find((c) => c.id === clientId);
    const clientName = cl?.name || null;
    setSelectedClientId(clientId);
    setSelectedClientName(clientName);
    if (selectedConvId) await updateConversation(selectedConvId, { client_id: clientId, client_name: clientName });
  };

  const handleSend = async (content, agentId = null, files = []) => {
    if (!content.trim() && files.length === 0) return;

    let convId = selectedConvId;
    if (!convId) {
      try {
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_email: userEmail, user_name: userName,
            agent_id: currentAgent?.id || null, model,
            folder_id: selectedFolderId, client_id: selectedClientId, client_name: selectedClientName,
          }),
        });
        if (res.ok) {
          const conv = await res.json();
          convId = conv.id;
          setSelectedConvId(convId);
        } else { toast.error('Gesprek aanmaken mislukt'); return; }
      } catch (err) { toast.error('Gesprek aanmaken mislukt'); return; }
    }

    const mentionedAgent = agentId ? agents.find((a) => a.id === agentId) : null;
    const displayContent = files.length > 0
      ? `${content}\n\n\u{1F4CE} Bijlagen: ${files.map((f) => f.name).join(', ')}`
      : content;
    const userMsg = { role: 'user', content: displayContent, id: `temp-user-${Date.now()}`, created_at: new Date().toISOString(), agent_id: agentId, agent_name: mentionedAgent?.name || null };
    const assistantMsg = { role: 'assistant', content: '', id: `temp-assistant-${Date.now()}`, isStreaming: true, created_at: new Date().toISOString(), agent_id: agentId, agent_name: mentionedAgent?.name || null };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      let res;

      if (files.length > 0) {
        // Use multipart upload endpoint
        const formData = new FormData();
        formData.append('content', content);
        formData.append('user_email', userEmail);
        if (agentId) formData.append('agent_id', String(agentId));
        for (const file of files) {
          formData.append('files', file);
        }
        res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations/${convId}/messages/upload`, {
          method: 'POST',
          // no Content-Type — browser sets multipart boundary
          body: formData,
          signal: controller.signal,
        });
      } else {
        // Use regular JSON endpoint
        res = await authFetch(`${AI_ENGINE_URL}/api/v1/chat/conversations/${convId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content, user_email: userEmail, agent_id: agentId }),
          signal: controller.signal,
        });
      }

      if (!res.ok) throw new Error(`API error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.error) { toast.error(data.error); break; }
            if (data.content) {
              fullContent += data.content;
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { ...updated[updated.length - 1], content: fullContent };
                return updated;
              });
            }
            if (data.done) {
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { ...updated[updated.length - 1], isStreaming: false };
                return updated;
              });
            }
          } catch (parseErr) { /* skip */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Er ging iets mis met het genereren van een antwoord');
        console.error('[AIChat] Stream error:', err);
      }
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && !last.content) return prev.slice(0, -1);
        return prev.map((m) => ({ ...m, isStreaming: false }));
      });
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
      fetchConversations();
    }
  };

  const handleStop = () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
  const handleSelectAgent = (agent) => {
    if (agent) {
      setAgentToInsert(agent);
    } else {
      setCurrentAgent(null);
    }
  };
  const handleSelectPrompt = (prompt) => { handleSend(prompt.content, prompt.agent_id || null); };

  // Determine if current folder has a project_id
  const currentFolder = selectedFolderId ? folders.find((f) => f.id === selectedFolderId) : null;
  const hasProjectId = !!(currentFolder?.project_id);

  // ─── Render ────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', position: 'relative' }}>
      <ChatSidebar
        conversations={conversations}
        archivedConversations={archivedConversations}
        showArchive={showArchive}
        onToggleArchive={() => setShowArchive(!showArchive)}
        selectedId={selectedConvId}
        onSelect={handleSelectConversation}
        onNewChat={() => handleNewChat(null)}
        onNewChatInFolder={handleNewChatInFolder}
        onDelete={handleDeleteConversation}
        onArchive={handleArchiveConversation}
        onUnarchive={handleUnarchiveConversation}
        agents={agents}
        prompts={prompts}
        onRefreshAgents={fetchAgents}
        onRefreshPrompts={fetchPrompts}
        userEmail={userEmail}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isMobile={isMobile}
        folders={folders}
        selectedFolderId={selectedFolderId}
        onSelectFolder={handleSelectFolder}
        onEditFolder={handleEditFolder}
        onDeleteFolder={handleDeleteFolder}
        onSearch={handleSearch}
        onPin={handlePin}
        onRename={handleRename}
        onDetachFromFolder={handleDetachFromFolder}
        onStartGroupChat={handleStartGroupChat}
        onMoveToFolder={handleMoveToFolder}
        onOpenProjectBrowser={() => setShowProjectBrowser(true)}
        onUnpinProject={handleUnpinProject}
        clients={clients}
      />
      {showProjectBrowser ? (
        <ProjectBrowser
          userEmail={userEmail}
          onPinProject={handlePinProject}
          onUnpinProject={handleUnpinProject}
          onSelectFolder={handleSelectFolder}
          onClose={() => setShowProjectBrowser(false)}
        />
      ) : (
        <ChatArea
          messages={messages}
          onSend={handleSend}
          isStreaming={isStreaming}
          onStop={handleStop}
          model={model}
          onModelChange={handleModelChange}
          models={models}
          onOpenPrompts={() => setPromptPickerOpen(true)}
          onOpenAgents={() => setAgentPickerOpen(true)}
          agentName={currentAgent?.name}
          agentIcon={currentAgent?.icon}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          isMobile={isMobile}
          agents={agents}
          clients={clients}
          selectedClientId={selectedClientId}
          onClientChange={handleClientChange}
          agentToInsert={agentToInsert}
          onAgentInserted={() => setAgentToInsert(null)}
          participants={participants}
          hasProjectId={hasProjectId}
          showProjectOverview={showProjectOverview}
          onToggleProjectOverview={() => setShowProjectOverview(!showProjectOverview)}
          folderName={currentFolder?.name || null}
          projectStatus={currentFolder?.project_status || null}
        />
      )}
      {showProjectOverview && hasProjectId && !showProjectBrowser && (
        <ProjectOverviewPanel
          folderId={selectedFolderId}
          onClose={() => setShowProjectOverview(false)}
        />
      )}
      <AgentPicker
        open={agentPickerOpen}
        onClose={() => setAgentPickerOpen(false)}
        agents={agents}
        onSelect={handleSelectAgent}
      />
      <PromptPicker
        open={promptPickerOpen}
        onClose={() => setPromptPickerOpen(false)}
        prompts={prompts}
        onSelect={handleSelectPrompt}
      />
      <FolderSettings
        open={folderSettingsOpen}
        onClose={() => setFolderSettingsOpen(false)}
        folder={editingFolder}
        agents={agents}
        clients={clients}
        userEmail={userEmail}
        onSaved={handleFolderSaved}
      />
    </Box>
  );
}
